import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Banner, Button } from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { formatIstDate } from '../lib/time.js';
import type { BillingStatus } from '../lib/types.js';
import { useOnline } from './media.js';

export function useBillingStatus(): BillingStatus | undefined {
  const auth = useAuth();
  const status = useQuery({
    queryKey: ['billing-status', auth.agencyId],
    queryFn: () => api<BillingStatus>('/billing/status'),
    enabled: auth.current !== null && auth.impersonation === null,
    refetchInterval: 60_000,
  });
  return status.data;
}

/** True when this agency cannot take writes now: read only, or a deletion is pending. */
export function isReadOnly(status: BillingStatus | undefined): boolean {
  return status !== undefined && (!status.writable || status.deletionRequestedAt !== null);
}

/** Impersonation, subscription and offline notices, stacked above the screen. */
export function Banners() {
  const { t } = useTranslation();
  const auth = useAuth();
  const online = useOnline();
  const s = useBillingStatus();
  const navigate = useNavigate();
  const date = (iso: string | null) => (iso === null ? '' : formatIstDate(iso));
  const link = auth.allowed('billing.manage') ? (
    <Link to="/billing?plans=1">{t('billing.goToBilling')}</Link>
  ) : null;
  return (
    <div className="banners">
      {auth.impersonation ? (
        <Banner
          tone="dark"
          action={
            <Button
              variant="text"
              onClick={() => {
                void navigate('/admin', { state: { leaveView: true } });
              }}
            >
              {t('admin.backToAdmin')}
            </Button>
          }
        >
          <span data-testid="impersonation-banner">
            {t('admin.impersonating', { reason: auth.impersonation.reason })}
          </span>
        </Banner>
      ) : null}
      {online ? null : <Banner tone="warning">{t('shell.offline')}</Banner>}
      {s === undefined || auth.impersonation || s.deletionRequestedAt === null ? null : (
        <Banner tone="error">
          <span data-testid="deletion-banner">
            {t('billing.deletionBanner', { date: date(s.purgeAfter) })}
          </span>{' '}
          {link}
        </Banner>
      )}
    </div>
  );
}

/**
 * The subscription notice (trial ending, payment due, read only), drawn under the top bar of a
 * screen, as the frames show it, not above the whole app.
 */
export function BillingBanner() {
  const { t } = useTranslation();
  const auth = useAuth();
  const s = useBillingStatus();
  const date = (iso: string | null) => (iso === null ? '' : formatIstDate(iso));
  if (s === undefined || auth.impersonation || s.deletionRequestedAt !== null) return null;
  const link = auth.allowed('billing.manage') ? (
    <Link to="/billing?plans=1">{t('billing.goToBilling')}</Link>
  ) : null;
  if (s.status === 'read_only') {
    return (
      <Banner tone="error">
        <span data-testid="billing-banner">{t('billing.readOnlyBanner')}</span> {link}
      </Banner>
    );
  }
  if (s.status === 'past_due') {
    return (
      <Banner tone="warning">
        <span data-testid="billing-banner">
          {t('billing.graceBanner', { date: date(s.graceEndsAt) })}
        </span>{' '}
        {link}
      </Banner>
    );
  }
  if (s.status === 'trialing' && s.trialDaysLeft !== null && s.trialDaysLeft <= 7) {
    return (
      <Banner tone="info">
        <span data-testid="billing-banner">
          {t('billing.trialBanner', { count: s.trialDaysLeft })}
        </span>{' '}
        {link}
      </Banner>
    );
  }
  return null;
}
